import * as React from 'react';
import TimeTaxSupportSystemClient from '../../../system';
import { PrimaryButton } from '@fluentui/react';
import { DateTime } from 'luxon';
import { DatePicker } from '@fluentui/react';
import { DateCustom } from '../../../lib/DateCustom';
import MessageCourier from '../../../lib/MessageCourier';
import useAsyncDataCourier from '../../../common/custom-hooks/useAsyncDataCourier';
import { ALL_SECTORS, OrganizationChartDropdown } from '../../Common/OrganizationChartDropdown';
import { User } from '../../../system/User';
import { useTranslation } from 'react-i18next';
import { getLocalizedStrings } from '../../../lib/LocalizedStringsForDatePicker';
import PaisDropdown, { OptionPais } from '../../../shared/components/PaisDropdown';
import EmpresaSelector, { OptionEmpresa } from '../../../shared/components/EmpresaSelector';

interface ICompaniesPanelSelector {
  onClickSearch: () => void;
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  onChangeCompany: (company: OptionEmpresa | null) => void;
  onChangeOrganizationNodeId: (id: string) => void;
  onChangeFromDate: (date: DateTime) => void;
  onChangePais?: (pais: OptionPais | null) => void;
  user: User;
  useAllSector?: boolean;
  showPeriodFilter?: boolean;
}

export const PanelCompaniesSelector = (props: ICompaniesPanelSelector) => {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = React.useState<DateTime | undefined>(undefined);
  const [shouldRefresh, setShouldRefresh] = React.useState<boolean>(false);
  const [paisId, setPaisId] = React.useState<string | null>(null);

  const nodeOfLoggedCol = useAsyncDataCourier(
    () => props.system.getOrganizationChartSystem().getNodeOf(props.user.getCollaborator()),
    [props.system, props.user],
    props.courier,
  );

  const nodes = useAsyncDataCourier(
    () => props.system.getOrganizationChartSystem().getAvailableNodesOf(props.user),
    [props.system, props.user],
    props.courier,
  );

  React.useEffect(() => {
    const setDefaultValues = async () => {
      setFromDate(DateTime.fromJSDate(DateCustom.getFechaOneMonthAgo()));
      props.onChangeFromDate(DateTime.fromJSDate(DateCustom.getFechaOneMonthAgo()));
      const nodeOfColLogged = await props.system.getOrganizationChartSystem().getNodeOf(props.user.getCollaborator());
      props.onChangeOrganizationNodeId(nodeOfColLogged.getId());
    };
    setDefaultValues();
  }, []);

  const onChangeDate = (date: Date | null | undefined, name: string) => {
    if (date) {
      if (name === 'from') {
        setFromDate(DateTime.fromJSDate(date));
        props.onChangeFromDate(DateTime.fromJSDate(date));
        setShouldRefresh(!shouldRefresh);
        if (props.onChangeFromDate) props.onChangeFromDate(DateTime.fromJSDate(date));
      }
    }
  };

  return (
    <div>
      {nodes && nodeOfLoggedCol && (
        <div className="ms-Grid-row mt-12">
          <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg2">
            <DatePicker
              strings={getLocalizedStrings()}
              label={t('Periodo')}
              formatDate={DateCustom.formatDateMMYYYY}
              value={fromDate ? fromDate.toJSDate() : undefined}
              onSelectDate={(e) => {
                onChangeDate(e, 'from');
              }}
              calendarProps={{
                isDayPickerVisible: false,
                showGoToToday: false,
              }}
            />
          </div>

          {props.onChangePais && (
            <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg2">
              <PaisDropdown
                system={props.system}
                label={t('País')}
                onChangeSelected={(pais) => {
                  if (props.onChangePais) {
                    props.onChangePais(pais);

                    const id = pais?.key ?? null;
                    setPaisId(id);

                    // limpiar empresa si cambia país
                    if (pais?.key !== '') {
                      props.onChangeCompany(null);
                    }
                  }
                }}
              />
            </div>
          )}

          <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg3">
            <EmpresaSelector
              system={props.system}
              label={t('Empresa')}
              paisId={paisId}
              period={fromDate}
              onChangeSelected={(empresa) => {
                props.onChangeCompany(empresa);
              }}
            />
          </div>

          <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg3">
            <OrganizationChartDropdown
              options={nodes || []}
              label={t('Sector')}
              onChange={(node) => {
                props.onChangeOrganizationNodeId(node.getId());
              }}
              defaultSelectedKey={nodeOfLoggedCol?.getId()}
              useAllSector={props.useAllSector && props.user.hasPowerUserPrivileges()}
              onSelectAll={() => props.onChangeOrganizationNodeId(ALL_SECTORS)}
            />
          </div>
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg2">
            <PrimaryButton
              iconProps={{ iconName: 'Search' }}
              className="mt-for-btn-search ml-2 panel-companies-search-btn"
              onClick={props.onClickSearch}
              text={t('Buscar')}
            />
          </div>
        </div>
      )}
    </div>
  );
};
